import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Plus, Trash2, Star, Camera, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

// Componente de selección múltiple con tarjetas
interface MultiSelectProps {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  isDark: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ 
  options, 
  selectedValues, 
  onChange, 
  placeholder = "Seleccionar opciones",
  isDark 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(v => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const handleRemoveOption = (option: string) => {
    onChange(selectedValues.filter(v => v !== option));
  };

  return (
    <div className="relative">
      {/* Campo principal */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2 rounded-xl border cursor-pointer flex items-center justify-between ${
          isDark 
            ? 'bg-slate-800 border-slate-700 text-white' 
            : 'bg-white border-gray-300 text-gray-900'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        <span className={selectedValues.length === 0 ? (isDark ? 'text-gray-400' : 'text-gray-500') : ''}>
          {selectedValues.length === 0 ? placeholder : `${selectedValues.length} opciones seleccionadas`}
        </span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {/* Tarjetas de opciones seleccionadas */}
      {selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedValues.map(option => (
            <div
              key={option}
              className={`px-3 py-1 rounded-lg flex items-center space-x-2 ${
                isDark 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              <span className="text-sm">{option}</span>
              <button
                onClick={() => handleRemoveOption(option)}
                className="hover:bg-black hover:bg-opacity-20 rounded-full p-0.5"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown de opciones */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`absolute z-10 w-full mt-1 rounded-xl border shadow-lg ${
              isDark 
                ? 'bg-slate-800 border-slate-700' 
                : 'bg-white border-gray-300'
            }`}
          >
            <div className="max-h-48 overflow-y-auto">
              {options.map(option => (
                <div
                  key={option}
                  onClick={() => handleToggleOption(option)}
                  className={`px-4 py-2 cursor-pointer hover:bg-opacity-50 ${
                    selectedValues.includes(option)
                      ? (isDark ? 'bg-blue-600 bg-opacity-50' : 'bg-blue-100')
                      : (isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100')
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedValues.includes(option)
                        ? (isDark ? 'bg-blue-600 border-blue-600' : 'bg-blue-500 border-blue-500')
                        : (isDark ? 'border-slate-500' : 'border-gray-300')
                    }`}>
                      {selectedValues.includes(option) && (
                        <X size={12} className={isDark ? 'text-white' : 'text-white'} />
                      )}
                    </div>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>{option}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Category {
  id: string;
  name: string;
  fields: Record<string, any>;
  enabled: boolean;
}

interface Variant {
  id: string;
  color: string;
  images: {
    front?: File;
    reverse?: File;
    closeup1?: File;
    closeup2?: File;
    closeup3?: File;
    closeup4?: File;
  };
  mainImage: 'front' | 'reverse' | null;
}

interface ProductFormData {
  category: string;
  nombre: string;
  sku: string;
  stock: number;
  precioCompra: number;
  precioVenta: number;
  fechaIngreso: string;
  marca: string;
  proveedor: string;
  genero: string[];
  temporada: string;
  variants: Variant[];
  // Campos dinámicos de la categoría
  [key: string]: any;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    category: '',
    nombre: '',
    sku: '',
    stock: 0,
    precioCompra: 0,
    precioVenta: 0,
    fechaIngreso: '',
    marca: '',
    proveedor: '',
    genero: [],
    temporada: '',
    variants: [{
      id: '1',
      color: '',
      images: {},
      mainImage: null
    }] // Primera variante por defecto
  });
  const [fieldSettings, setFieldSettings] = useState<Record<string, Record<string, boolean>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Debug: Log de categorías para diagnóstico
  useEffect(() => {
    console.log('🔍 AddProductModal - Categorías cargadas:', categories.length);
    console.log('🔍 AddProductModal - Lista de categorías:', categories.map(cat => cat.name));
  }, [categories]);

  // Función para formatear números con separadores de miles
  const formatNumber = (value: number): string => {
    if (value >= 1000) {
      return value.toLocaleString('es-ES');
    }
    return value.toString();
  };

  // Función para parsear números desde string formateado
  const parseFormattedNumber = (value: string): number => {
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
  };

  // Función para manejar cambios en campos numéricos
  const handleNumericChange = (field: string, value: string) => {
    const numericValue = parseFormattedNumber(value);
    setFormData(prev => ({ ...prev, [field]: numericValue }));
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      console.log('🔍 Cargando categorías...');
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      console.log('✅ Servicio optimizado cargado');
      
      const categoriesData = await optimizedService.getCategories();
      console.log('📊 Categorías obtenidas:', categoriesData.length);
      console.log('📋 Lista de categorías:', categoriesData);
      
      setCategories(categoriesData);
      
      // Cargar configuración de campos
      const initialFieldSettings: Record<string, Record<string, boolean>> = {};
      categoriesData.forEach(category => {
        initialFieldSettings[category.name] = {};
        Object.keys(category.fields || {}).forEach(fieldName => {
          const globalFields = ['sku', 'stock', 'precioCompra', 'precioVenta', 'fechaIngreso', 'marca', 'proveedor', 'genero', 'temporada'];
          initialFieldSettings[category.name][fieldName] = globalFields.includes(fieldName) || true;
        });
      });
      setFieldSettings(initialFieldSettings);
      
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      console.log('🔄 Abriendo AddProductModal, cargando categorías...');
      loadCategories();
    }
  }, [isOpen, user]);

  // Efecto adicional para asegurar que las categorías se carguen
  useEffect(() => {
    if (isOpen && user && categories.length === 0) {
      console.log('⚠️ No hay categorías cargadas en AddProductModal, intentando cargar nuevamente...');
      setTimeout(() => {
        loadCategories();
      }, 1000);
    }
  }, [isOpen, user, categories.length]);

  const handleCloseModal = () => {
    setFormData({
      category: '',
      nombre: '',
      sku: '',
      stock: 0,
      precioCompra: 0,
      precioVenta: 0,
      fechaIngreso: '',
      marca: '',
      proveedor: '',
      genero: [],
      temporada: '',
      variants: [{
        id: '1',
        color: '',
        images: {},
        mainImage: null
      }]
    });
    setErrors({});
    onClose();
  };

  const handleCategoryChange = (categoryName: string) => {
    setFormData(prev => ({
      ...prev,
      category: categoryName,
      variants: prev.variants.length > 0 ? prev.variants : [{
        id: '1',
        color: '',
        images: {},
        mainImage: null
      }]
    }));
    setErrors(prev => ({ ...prev, category: '' }));
  };

  const addVariant = () => {
    const newVariant: Variant = {
      id: Date.now().toString(),
      color: '',
      images: {},
      mainImage: null
    };
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));
  };

  const removeVariant = (variantId: string) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(v => v.id !== variantId)
    }));
  };

  const updateVariantColor = (variantId: string, color: string) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => 
        v.id === variantId ? { ...v, color } : v
      )
    }));
  };

  const handleImageUpload = (variantId: string, imageType: string, file: File) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => 
        v.id === variantId 
          ? { 
              ...v, 
              images: { 
                ...v.images, 
                [imageType]: file 
              } 
            } 
          : v
      )
    }));
  };

  const handleImageRemove = (variantId: string, imageType: string) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => 
        v.id === variantId 
          ? { 
              ...v, 
              images: { 
                ...v.images, 
                [imageType]: undefined 
              },
              mainImage: v.mainImage === imageType ? null : v.mainImage
            } 
          : v
      )
    }));
  };

  const setMainImage = (variantId: string, imageType: 'front' | 'reverse') => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => 
        v.id === variantId 
          ? { ...v, mainImage: imageType } 
          : v
      )
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const selectedCategory = categories.find(cat => cat.name === formData.category);
    
    if (!formData.category) {
      newErrors.category = 'Selecciona una categoría';
    }

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre del producto es obligatorio';
    }

    // Generar SKU automáticamente
    if (!formData.sku.trim()) {
      formData.sku = generateSKU(formData.category);
    }

    if (formData.stock <= 0) {
      newErrors.stock = 'El stock debe ser mayor a 0';
    }

    if (formData.precioCompra <= 0) {
      newErrors.precioCompra = 'El precio de compra debe ser mayor a 0';
    }

    if (formData.precioVenta <= 0) {
      newErrors.precioVenta = 'El precio de venta debe ser mayor a 0';
    }

    if (!formData.fechaIngreso) {
      newErrors.fechaIngreso = 'La fecha de ingreso es obligatoria';
    }

    // Marca y proveedor son opcionales

    if (formData.genero.length === 0) {
      newErrors.genero = 'Selecciona al menos un género';
    }

    if (!formData.temporada) {
      newErrors.temporada = 'La temporada es obligatoria';
    }

    // Validar campos específicos de la categoría
    if (selectedCategory) {
      Object.entries(selectedCategory.fields || {}).forEach(([fieldName, fieldConfig]) => {
        const isRequired = fieldSettings[formData.category]?.[fieldName];
        if (isRequired && (!formData[fieldName] || formData[fieldName] === '')) {
          newErrors[fieldName] = `${fieldName} es obligatorio`;
        }
      });
    }

    // Validar variantes
    if (formData.variants.length === 0) {
      newErrors.variants = 'Debes agregar al menos una variante';
    } else {
      formData.variants.forEach((variant, index) => {
        if (!variant.color.trim()) {
          newErrors[`variant_${index}_color`] = 'El color es obligatorio';
        }
        if (!variant.images.front) {
          newErrors[`variant_${index}_front`] = 'La foto frontal es obligatoria';
        }
        if (!variant.images.reverse) {
          newErrors[`variant_${index}_reverse`] = 'La foto del reverso es obligatoria';
        }
        if (!variant.mainImage) {
          newErrors[`variant_${index}_main`] = 'Selecciona una foto principal';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateSKU = (categoryName: string): string => {
    const timestamp = Date.now().toString().slice(-6);
    const categoryPrefix = categoryName.substring(0, 3).toUpperCase();
    return `${categoryPrefix}${timestamp}`;
  };

  const getStandardizedId = (categoryName: string): string => {
    const mapping: Record<string, string> = {
      'CAMPERA': 'campera',
      'BUZO': 'buzo',
      'CAMISA': 'camisa',
      'REMERA': 'remera',
      'PANTALÓN': 'pantalon',
      'POLLERA': 'pollera',
      'VESTIDO': 'vestido',
      'PAÑUELO': 'pañuelo',
      'SOMBRERO, GORRA, GORRO': 'sgg',
      'CALZADO': 'calzado'
    };
    return mapping[categoryName] || categoryName.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const uploadImageToStorage = async (file: File, path: string): Promise<string> => {
    const { storage, ref, uploadBytes, getDownloadURL } = (window as any).auth;
    const imageRef = ref(storage, path);
    await uploadBytes(imageRef, file);
    return getDownloadURL(imageRef);
  };

  const saveProduct = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      
      // Generar SKU si no existe
      if (!formData.sku) {
        formData.sku = generateSKU(formData.category);
      }

      // Preparar datos del producto
      const productData: any = {
        nombre: formData.nombre,
        sku: formData.sku,
        stock: formData.stock,
        precioCompra: formData.precioCompra,
        precioVenta: formData.precioVenta,
        fechaIngreso: formData.fechaIngreso,
        marca: formData.marca,
        proveedor: formData.proveedor,
        genero: formData.genero,
        temporada: formData.temporada,
        variants: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Agregar campos específicos de la categoría
      const selectedCategory = categories.find(cat => cat.name === formData.category);
      if (selectedCategory) {
        Object.keys(selectedCategory.fields).forEach(fieldName => {
          if (formData[fieldName] !== undefined) {
            productData[fieldName] = formData[fieldName];
          }
        });
      }

      // Subir imágenes y preparar variantes
      for (const variant of formData.variants) {
        const variantData: any = {
          color: variant.color,
          images: {},
          mainImage: variant.mainImage
        };

        // Subir cada imagen
        for (const [imageType, file] of Object.entries(variant.images)) {
          if (file) {
            const isPrincipal = variant.mainImage === imageType;
            // Usar standardizedId para consistencia con la estructura de datos
            const standardizedId = getStandardizedId(formData.category);
            const imagePath = `users/${user?.uid}/categories/${standardizedId}/stock/${formData.sku}/${formData.sku}_${variant.color}_${imageType}${isPrincipal ? '_principal' : ''}`;
            const imageUrl = await uploadImageToStorage(file, imagePath);
            variantData.images[imageType] = imageUrl;
          }
        }

        productData.variants.push(variantData);
      }

      // Guardar producto en Firestore
      await optimizedService.createProduct(formData.category, productData);
      
      console.log('✅ Producto guardado exitosamente');
      handleCloseModal();
      
    } catch (error) {
      console.error('❌ Error guardando producto:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find(cat => cat.name === formData.category);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={handleCloseModal}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed inset-4 z-50 rounded-2xl shadow-2xl ${
              isDark ? 'bg-slate-900' : 'bg-white'
            }`}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <h2 className={`text-2xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Agregar Producto
                </h2>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-lg ${
                    isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <X className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Cargando categorías...
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto space-y-6">
                    {/* Selección de Categoría */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Categoría *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className={`w-full px-4 py-2 rounded-xl border ${
                          isDark 
                            ? 'bg-slate-800 border-slate-700 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        <option value="">Seleccionar categoría</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-1 text-center ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        El código SKU se generará automáticamente una vez creado el producto
                      </p>
                      {errors.category && (
                        <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                      )}
                    </div>

                    {/* Nombre del Producto */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Nombre del Producto *
                      </label>
                      <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                        className={`w-full px-4 py-2 rounded-xl border ${
                          isDark 
                            ? 'bg-slate-800 border-slate-700 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Ingresa el nombre del producto"
                      />
                      {errors.nombre && (
                        <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>
                      )}
                    </div>

                    {/* Campos Globales */}
                    {formData.category && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Stock */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Stock *
                            </label>
                            <input
                              type="text"
                              value={formData.stock > 0 ? formatNumber(formData.stock) : ''}
                              onChange={(e) => handleNumericChange('stock', e.target.value)}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder="0"
                            />
                            {errors.stock && (
                              <p className="text-red-500 text-sm mt-1">{errors.stock}</p>
                            )}
                          </div>

                          {/* Precio Compra */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Precio de Compra *
                            </label>
                            <div className="relative">
                              <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                $
                              </span>
                              <input
                                type="text"
                                value={formData.precioCompra > 0 ? formatNumber(formData.precioCompra) : ''}
                                onChange={(e) => handleNumericChange('precioCompra', e.target.value)}
                                className={`w-full pl-8 pr-4 py-2 rounded-xl border ${
                                  isDark 
                                    ? 'bg-slate-800 border-slate-700 text-white' 
                                    : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                placeholder="0"
                              />
                            </div>
                            {errors.precioCompra && (
                              <p className="text-red-500 text-sm mt-1">{errors.precioCompra}</p>
                            )}
                          </div>

                          {/* Precio Venta */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Precio de Venta *
                            </label>
                            <div className="relative">
                              <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                $
                              </span>
                              <input
                                type="text"
                                value={formData.precioVenta > 0 ? formatNumber(formData.precioVenta) : ''}
                                onChange={(e) => handleNumericChange('precioVenta', e.target.value)}
                                className={`w-full pl-8 pr-4 py-2 rounded-xl border ${
                                  isDark 
                                    ? 'bg-slate-800 border-slate-700 text-white' 
                                    : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                placeholder="0"
                              />
                            </div>
                            {errors.precioVenta && (
                              <p className="text-red-500 text-sm mt-1">{errors.precioVenta}</p>
                            )}
                          </div>

                          {/* Fecha Ingreso */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Fecha de Ingreso *
                            </label>
                            <input
                              type="date"
                              value={formData.fechaIngreso}
                              onChange={(e) => setFormData(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {errors.fechaIngreso && (
                              <p className="text-red-500 text-sm mt-1">{errors.fechaIngreso}</p>
                            )}
                          </div>

                          {/* Marca */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Marca
                            </label>
                            <input
                              type="text"
                              value={formData.marca}
                              onChange={(e) => setFormData(prev => ({ ...prev, marca: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {errors.marca && (
                              <p className="text-red-500 text-sm mt-1">{errors.marca}</p>
                            )}
                          </div>

                          {/* Proveedor */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Proveedor
                            </label>
                            <input
                              type="text"
                              value={formData.proveedor}
                              onChange={(e) => setFormData(prev => ({ ...prev, proveedor: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {errors.proveedor && (
                              <p className="text-red-500 text-sm mt-1">{errors.proveedor}</p>
                            )}
                          </div>

                          {/* Género */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Género *
                            </label>
                            <MultiSelect
                              options={['Hombre', 'Mujer', 'Niños', 'Niñas', 'Unisex']}
                              selectedValues={formData.genero}
                              onChange={(values) => setFormData(prev => ({ ...prev, genero: values }))}
                              placeholder="Seleccionar géneros"
                              isDark={isDark}
                            />
                            {errors.genero && (
                              <p className="text-red-500 text-sm mt-1">{errors.genero}</p>
                            )}
                          </div>

                          {/* Temporada */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Temporada *
                            </label>
                            <select
                              value={formData.temporada}
                              onChange={(e) => setFormData(prev => ({ ...prev, temporada: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            >
                              <option value="">Seleccionar temporada</option>
                              <option value="Verano">Verano</option>
                              <option value="Otoño">Otoño</option>
                              <option value="Invierno">Invierno</option>
                              <option value="Primavera">Primavera</option>
                              <option value="Atemporal">Atemporal</option>
                            </select>
                            {errors.temporada && (
                              <p className="text-red-500 text-sm mt-1">{errors.temporada}</p>
                            )}
                          </div>
                        </div>

                        {/* Campos Específicos de la Categoría */}
                        {selectedCategory && (
                          <div>
                            <h3 className={`text-lg font-semibold mb-4 ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              Campos Específicos de {selectedCategory.name}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {Object.entries(selectedCategory.fields).map(([fieldName, fieldConfig]) => {
                                const isRequired = fieldSettings[formData.category]?.[fieldName];
                                const globalFields = ['sku', 'stock', 'precioCompra', 'precioVenta', 'fechaIngreso', 'marca', 'proveedor', 'genero', 'temporada'];
                                
                                if (globalFields.includes(fieldName)) {
                                  return null; // Skip global fields
                                }

                                return (
                                  <div key={fieldName}>
                                    <label className={`block text-sm font-medium mb-2 ${
                                      isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      {fieldName} {isRequired && <span className="text-red-500">*</span>}
                                    </label>
                                    
                                    {fieldConfig.type === 'select' ? (
                                      ['talle', 'genero', 'tipo', 'estilo'].includes(fieldName.toLowerCase()) ? (
                                        <MultiSelect
                                          options={fieldConfig.options || []}
                                          selectedValues={formData[fieldName] || []}
                                          onChange={(values) => setFormData(prev => ({ ...prev, [fieldName]: values }))}
                                          placeholder={`Seleccionar ${fieldName}`}
                                          isDark={isDark}
                                        />
                                      ) : (
                                        <select
                                          value={formData[fieldName] || ''}
                                          onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                          className={`w-full px-4 py-2 rounded-xl border ${
                                            isDark 
                                              ? 'bg-slate-800 border-slate-700 text-white' 
                                              : 'bg-white border-gray-300 text-gray-900'
                                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                        >
                                          <option value="">Seleccionar {fieldName}</option>
                                          {fieldConfig.options?.map((option: string) => (
                                            <option key={option} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      )
                                    ) : fieldConfig.type === 'number' ? (
                                      <input
                                        type="number"
                                        value={formData[fieldName] || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: parseFloat(e.target.value) || 0 }))}
                                        className={`w-full px-4 py-2 rounded-xl border ${
                                          isDark 
                                            ? 'bg-slate-800 border-slate-700 text-white' 
                                            : 'bg-white border-gray-300 text-gray-900'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        value={formData[fieldName] || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                        className={`w-full px-4 py-2 rounded-xl border ${
                                          isDark 
                                            ? 'bg-slate-800 border-slate-700 text-white' 
                                            : 'bg-white border-gray-300 text-gray-900'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                      />
                                    )}
                                    
                                    {errors[fieldName] && (
                                      <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Variantes */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-lg font-semibold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              Variantes *
                            </h3>
                            <button
                              onClick={addVariant}
                              className={`px-4 py-2 rounded-xl ${
                                isDark 
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                              } flex items-center space-x-2`}
                            >
                              <Plus className="w-4 h-4" />
                              <span>Agregar Variante</span>
                            </button>
                          </div>

                          {formData.variants.map((variant, index) => (
                            <div key={variant.id} className={`p-4 rounded-xl border mb-4 ${
                              isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                            }`}>
                              <div className="flex items-center justify-between mb-4">
                                <h4 className={`font-medium ${
                                  isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                  Variante {index + 1}
                                </h4>
                                {index > 0 && (
                                  <button
                                    onClick={() => removeVariant(variant.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>

                              {/* Color */}
                              <div className="mb-4">
                                <label className={`block text-sm font-medium mb-2 ${
                                  isDark ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                  Color *
                                </label>
                                <input
                                  type="text"
                                  value={variant.color}
                                  onChange={(e) => updateVariantColor(variant.id, e.target.value)}
                                  className={`w-full px-4 py-2 rounded-xl border ${
                                    isDark 
                                      ? 'bg-slate-800 border-slate-700 text-white' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                  placeholder="Ej: Rojo, Azul, Negro..."
                                />
                                {errors[`variant_${index}_color`] && (
                                  <p className="text-red-500 text-sm mt-1">{errors[`variant_${index}_color`]}</p>
                                )}
                              </div>

                              {/* Imágenes */}
                              <div>
                                <h5 className={`font-medium mb-3 ${
                                  isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                  Imágenes *
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  {/* Frente */}
                                  <div className="text-center">
                                    <label className={`block text-sm font-medium mb-2 ${
                                      isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      Frente *
                                    </label>
                                    <div className={`border-2 border-dashed rounded-xl p-4 ${
                                      variant.images.front 
                                        ? (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50')
                                        : (isDark ? 'border-slate-600' : 'border-gray-300')
                                    }`}>
                                      {variant.images.front ? (
                                        <div className="space-y-2">
                                          <div className="relative">
                                            <img 
                                              src={URL.createObjectURL(variant.images.front)} 
                                              alt="Preview frontal" 
                                              className="w-full h-20 object-contain rounded-lg"
                                            />
                                            <button
                                              onClick={() => handleImageRemove(variant.id, 'front')}
                                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                            >
                                              <X size={12} />
                                            </button>
                                          </div>
                                          <button
                                            onClick={() => setMainImage(variant.id, 'front')}
                                            className={`px-2 py-1 rounded text-xs ${
                                              variant.mainImage === 'front'
                                                ? 'bg-yellow-500 text-white'
                                                : 'bg-gray-500 text-white hover:bg-gray-600'
                                            }`}
                                          >
                                            <Star className="w-3 h-3 inline mr-1" />
                                            Principal
                                          </button>
                                        </div>
                                      ) : (
                                        <label className="cursor-pointer">
                                          <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) handleImageUpload(variant.id, 'front', file);
                                            }}
                                            className="hidden"
                                          />
                                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                          <p className="text-sm text-gray-400">Subir imagen</p>
                                        </label>
                                      )}
                                    </div>
                                    {errors[`variant_${index}_front`] && (
                                      <p className="text-red-500 text-sm mt-1">{errors[`variant_${index}_front`]}</p>
                                    )}
                                  </div>

                                  {/* Reverso */}
                                  <div className="text-center">
                                    <label className={`block text-sm font-medium mb-2 ${
                                      isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      Reverso *
                                    </label>
                                    <div className={`border-2 border-dashed rounded-xl p-4 ${
                                      variant.images.reverse 
                                        ? (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50')
                                        : (isDark ? 'border-slate-600' : 'border-gray-300')
                                    }`}>
                                      {variant.images.reverse ? (
                                        <div className="space-y-2">
                                          <div className="relative">
                                            <img 
                                              src={URL.createObjectURL(variant.images.reverse)} 
                                              alt="Preview reverso" 
                                              className="w-full h-20 object-contain rounded-lg"
                                            />
                                            <button
                                              onClick={() => handleImageRemove(variant.id, 'reverse')}
                                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                            >
                                              <X size={12} />
                                            </button>
                                          </div>
                                          <button
                                            onClick={() => setMainImage(variant.id, 'reverse')}
                                            className={`px-2 py-1 rounded text-xs ${
                                              variant.mainImage === 'reverse'
                                                ? 'bg-yellow-500 text-white'
                                                : 'bg-gray-500 text-white hover:bg-gray-600'
                                            }`}
                                          >
                                            <Star className="w-3 h-3 inline mr-1" />
                                            Principal
                                          </button>
                                        </div>
                                      ) : (
                                        <label className="cursor-pointer">
                                          <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) handleImageUpload(variant.id, 'reverse', file);
                                            }}
                                            className="hidden"
                                          />
                                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                          <p className="text-sm text-gray-400">Subir imagen</p>
                                        </label>
                                      )}
                                    </div>
                                    {errors[`variant_${index}_reverse`] && (
                                      <p className="text-red-500 text-sm mt-1">{errors[`variant_${index}_reverse`]}</p>
                                    )}
                                  </div>

                                  {/* Close-ups */}
                                  {['closeup1', 'closeup2', 'closeup3', 'closeup4'].map(closeup => (
                                    <div key={closeup} className="text-center">
                                      <label className={`block text-sm font-medium mb-2 ${
                                        isDark ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        {closeup.charAt(0).toUpperCase() + closeup.slice(1)}
                                      </label>
                                      <div className={`border-2 border-dashed rounded-xl p-4 ${
                                        variant.images[closeup as keyof typeof variant.images]
                                          ? (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50')
                                          : (isDark ? 'border-slate-600' : 'border-gray-300')
                                      }`}>
                                        {variant.images[closeup as keyof typeof variant.images] ? (
                                          <div className="space-y-2">
                                            <div className="relative">
                                              <img 
                                                src={URL.createObjectURL(variant.images[closeup as keyof typeof variant.images]!)} 
                                                alt={`Preview ${closeup}`} 
                                                className="w-full h-20 object-contain rounded-lg"
                                              />
                                              <button
                                                onClick={() => handleImageRemove(variant.id, closeup)}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                              >
                                                <X size={12} />
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <label className="cursor-pointer">
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleImageUpload(variant.id, closeup, file);
                                              }}
                                              className="hidden"
                                            />
                                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                            <p className="text-sm text-gray-400">Subir imagen</p>
                                          </label>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {errors[`variant_${index}_main`] && (
                                  <p className="text-red-500 text-sm mt-2">{errors[`variant_${index}_main`]}</p>
                                )}
                              </div>
                            </div>
                          ))}

                          {errors.variants && (
                            <p className="text-red-500 text-sm mt-2">{errors.variants}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`flex justify-end space-x-4 p-6 border-t ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <button
                  onClick={handleCloseModal}
                  className={`px-6 py-2 rounded-xl ${
                    isDark 
                      ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Cancelar
                </button>
                <button
                  onClick={saveProduct}
                  disabled={saving}
                  className={`px-6 py-2 rounded-xl ${
                    saving
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : isDark 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {saving ? 'Guardando...' : 'Guardar Producto'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddProductModal;